import {
    useState, useCallback, ChangeEvent, useEffect, useMemo,
} from 'react'
import noop from 'lodash/noop'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import isEqual from 'lodash/isEqual'
import {
    MultiActionButtonConfig,
} from 'app/shared-components/MultiActionButton/MultiActionButton.types'
import MultiActionButton from 'app/shared-components/MultiActionButton'
import usePaginationStyles from 'app/tss/pagination.styles'

import Card from 'app/shared-components/Card'
import {
    FilterPayload,
    useFilterContext,
} from 'app/hooks/useFilter'
import HistoryBlockPrompt from 'app/shared-components/HistoryBlockPrompt'
import UnsavedChangesPrompt from 'app/shared-components/UnsavedChangesPrompt'
import useBeforeUnloadDialog from 'app/hooks/useBeforeUnloadDialog'
import {
    AsyncMutationOptions,
} from 'app/types/request.types'
import SkyNetPagination, {
    useSkyNetPaginationContext,
} from 'app/shared-components/SkyNetPagination'

import useSyncState from 'app/hooks/useSyncState'
import {
    SkyNetSpreadsheetValidationContext,
    useSkyNetSpreadSheetValidation,
} from '../SkyNetSpreadsheetValidation'

import useStyles from './SkyNetSpreadSheetControlPanel.styles'
import PreselectFilterTabs from './PreselectFilterTabs'

export default function SkyNetSpreadSheetControlPanel({
    children,
    onSave,
    onCancel,
    edited = false,
    isFullScreen,
}: Readonly<{
    children: JSX.Element,
    onSave?: (o?: AsyncMutationOptions) => void,
    onCancel?: (...args: any[]) => void,
    edited?: boolean,
    isFullScreen?: boolean,
}>) {
    const {
        classes, cx,
    } = useStyles()
    const {
        classes: paginationStyles,
    } = usePaginationStyles()

    const filterContext = useFilterContext()
    const {
        value: filterState,
    } = useSyncState(filterContext?.filter)
    const validationContext = useSkyNetSpreadSheetValidation()
    const [
        pending,
        setPending,
    ] = useState<boolean>(false)

    const stopPending = useCallback(() => {
        setPending(false)
    }, [])

    const [
        preselectOption,
        setPreselectOption,
    ] = useState<FilterPayload>(undefined)

    const closeConfirm = useCallback(() => {
        setPreselectOption(undefined)
    }, [setPreselectOption])

    let preselectTabs = []

    const handleSave = useCallback(({
        onSuccess,
    } = {} as AsyncMutationOptions) => {
        if (!validationContext.valid) {
            validationContext.setShowError(true)
            return undefined
        }
        setPending(true)

        return onSave({
            onSuccess,
            onSettled: stopPending,
        })
    }, [
        onSave,
        stopPending,
        validationContext,
    ])

    const setFilter = useCallback((filter: FilterPayload) => {
        filterContext.clearFilters()
        filterContext.setFilter(filter || preselectOption)
    }, [
        filterContext,
        preselectOption,
    ])

    const saveBefore = useCallback((onSuccess: (params?: any) => void) => {
        return () => {
            onSave({
                onSuccess,
                onSettled: stopPending,
            })
        }
    }, [
        onSave,
        stopPending,
    ])

    if (filterContext) {
        preselectTabs = Object.entries(filterContext?.config).reduce((acc, [
            key,
            value,
        ]) => {
            const {
                preselectOptions, filterField,
            } = value || {}

            if (preselectOptions) {
                const filterValue = filterContext.filter?.[filterField]

                return [
                    ...acc,
                    {
                        name: key,
                        filterField,
                        value: filterValue,
                        ...preselectOptions,
                    },
                ]
            }
            return acc
        }, [])
    }

    const pagination = useSkyNetPaginationContext()

    const [
        pageChangedTo,
        setPageChangedTo,
    ] = useState<number>()

    const onChangePage = useCallback((e: ChangeEvent<HTMLInputElement>, page: number) => {
        if (!edited) {
            return pagination?.onChange(e, page)
        }

        if (pagination?.page && page !== pagination.page) {
            setPageChangedTo(page)
        }

        return undefined
    }, [
        edited,
        pagination,
    ])

    const proceedPageChanged = useCallback(() => {
        pagination?.onChange(null, pageChangedTo)
        setPageChangedTo(undefined)
    }, [
        pageChangedTo,
        pagination,
    ])

    useBeforeUnloadDialog(edited)

    /*
    * discard page to 1 when new filter applied
    * */
    useEffect(() => {
        if (!isEqual(filterContext?.filter, filterState) && pagination?.page !== 1) {
            onChangePage(null, 1)
        }
    }, [filterContext?.filter]) // eslint-disable-line react-hooks/exhaustive-deps

    const saveEnabled = Boolean(edited && !pending)

    const saveItemsConfig = useMemo(() => {
        return (saveEnabled ? [
            {
                label: 'Save',
                handler: () => {
                    return handleSave({
                        onSuccess: noop,
                    })
                },
            },
            {
                label: 'Save And Close',
                handler: handleSave,
                addDividerBefore: true,
            },
        ] : []) as MultiActionButtonConfig[]
    }, [
        handleSave,
        saveEnabled,
    ])

    return (
        <div className={classes.wrapper}>
            <div className={cx(classes.header, {
                [classes.preselect]: preselectTabs.length > 0,
            })}
            >
                {preselectTabs.length > 0
                    ? (
                        <PreselectFilterTabs
                            preselectTabs={preselectTabs}
                            setFilter={edited ? setPreselectOption : setFilter}
                        />
                    ) : null}
                <div className={classes.controls}>
                    {onSave ? (
                        <MultiActionButton
                            mainElement={(
                                <SaveIcon
                                    className={cx(classes.icon, {
                                        [classes.iconDisabled]: !saveEnabled,
                                    })}
                                />
                            )}
                            itemsConfig={saveItemsConfig}
                        />
                    ) : null}
                    {onCancel ? (
                        <CloseIcon
                            className={classes.icon}
                            onClick={onCancel}
                        />
                    ) : null}
                </div>
            </div>
            <Card contentClass={classes.content}>
                <SkyNetSpreadsheetValidationContext.Provider value={validationContext}>
                    {children}
                </SkyNetSpreadsheetValidationContext.Provider>
            </Card>
            {pagination
                ? (
                    <div className={paginationStyles.paginationContainer}>
                        <SkyNetPagination
                            total={pagination.matches}
                            rowsPerPage={pagination.rows}
                            page={pagination.page}
                            onChange={onChangePage}
                        />
                    </div>
                )
                : null}
            {isFullScreen && (
                <div
                    className={classes.fullScreenFooter}
                    data-testid="spreadsheet-footer"
                />
            )}
            <HistoryBlockPrompt
                when={!pending && edited}
                action={handleSave}
                discard={onCancel || noop}
            />
            <UnsavedChangesPrompt
                open={Boolean(preselectOption)}
                close={closeConfirm}
                action={saveBefore(setFilter)}
                discard={setFilter}
            />
            <UnsavedChangesPrompt
                open={Boolean(pageChangedTo)}
                close={setPageChangedTo}
                action={saveBefore(proceedPageChanged)}
                discard={proceedPageChanged}
            />
        </div>
    )
}
